import { ApiError, Button, ErrorNote, Field, Loading, Note } from "@corecrm/ui";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useAnswerFeedback, useFeedbackForm, useMe } from "../api/queries";
import type { FeedbackForm } from "../api/types";
import { PublicShell } from "../components/bits";

const TITLE = "Tell us how we did";
export const SCORES = Array.from({ length: 11 }, (_, i) => i);

/* /feedback/<token>: the link in a feedback request email (UX-044), and the "Waiting on you"
   item on Home. Opens without signing in: how likely are you to recommend us, 0 to 10, and why. */
export function FeedbackPage() {
  const { token = "" } = useParams();
  const form = useFeedbackForm(token);
  if (form.isPending)
    return (
      <PublicShell title={TITLE}>
        <Loading />
      </PublicShell>
    );
  if (form.error)
    return (
      <PublicShell title={TITLE}>
        <h1>We couldn't open this link</h1>
        {form.error instanceof ApiError && form.error.status === 400 ? (
          <p className="muted">This feedback link isn't valid any more. Thank you for thinking of us.</p>
        ) : (
          <ErrorNote error={form.error} />
        )}
      </PublicShell>
    );
  return <Form token={token} form={form.data} />;
}

function Form({ token, form }: { token: string; form: FeedbackForm }) {
  const answer = useAnswerFeedback(token);
  const signedIn = !!useMe().data;
  const [score, setScore] = useState<number | null>(null);
  const [comment, setComment] = useState("");
  const about = form.project_name ? ` on ${form.project_name}` : "";
  if (form.answered)
    return (
      <PublicShell title={TITLE}>
        <h1>Thank you, {form.first_name}</h1>
        <Note kind="acc">
          Your feedback{form.score !== null && form.score !== undefined ? ` (${form.score}/10)` : ""} reached{" "}
          {form.company_name}. We read every answer.
        </Note>
        {signedIn && (
          <p>
            <Link className="btn" to="/">
              Back to your portal
            </Link>
          </p>
        )}
      </PublicShell>
    );
  return (
    <PublicShell title={TITLE}>
      <h1>How did we do{about}?</h1>
      <p className="muted">
        Hi {form.first_name}, thank you for working with {form.company_name}. It takes less than a minute.
      </p>
      <fieldset className="nps">
        <legend>How likely are you to recommend us to a friend or colleague?</legend>
        <div className="nps-scale" role="radiogroup" aria-label="Score from 0 to 10">
          {SCORES.map((n) => (
            <button
              key={n}
              type="button"
              role="radio"
              aria-checked={score === n}
              className={score === n ? "btn primary" : "btn"}
              onClick={() => setScore(n)}
            >
              {n}
            </button>
          ))}
        </div>
        <div className="nps-ends faint">
          <span>Not at all likely</span>
          <span>Extremely likely</span>
        </div>
      </fieldset>
      <Field label="What's the main reason for your score? (optional)" full>
        <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={4} maxLength={5000} />
      </Field>
      {answer.error && <ErrorNote error={answer.error} />}
      <div className="row">
        <Button
          variant="primary"
          busy={answer.isPending}
          disabled={score === null}
          onClick={() => score !== null && answer.mutate({ score, comment: comment.trim() || null })}
        >
          Send feedback
        </Button>
      </div>
    </PublicShell>
  );
}
